/**
 * AttachmentsCard Component
 *
 * Displays file attachments for one or more entities (e.g., booking + vehicle).
 * Supports upload, preview, tag management, and deletion.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Button,
  Collapse,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useMultiEntityAttachments, useDeleteAttachment } from '@/hooks/useAttachments';
import type { EntityRef } from '@/hooks/useAttachments';
import type { Attachment, AttachmentEntityType } from '@/types';
import { useNotify } from '@/hooks/useNotify';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { api } from '@/lib/api';
import { AttachmentUploadDialog, type OptionalUploadLink } from './AttachmentUploadDialog';
import { AttachmentDetailDialog } from './AttachmentDetailDialog';

interface AttachmentsCardProps {
  /** Entities to FETCH attachments by (an attachment linked to any of these is shown). */
  entityLinks: EntityRef[];
  /**
   * Entities a newly uploaded file is linked to. Defaults to {@link entityLinks}.
   * Set this when the fetch set is broader than the upload target — e.g. the
   * vehicle tab fetches by [vehicle, ...its bookings] but uploads only to the
   * vehicle (#1043).
   */
  uploadEntityLinks?: EntityRef[];
  /**
   * Whether a given attachment can be modified (deleted / re-tagged) from THIS
   * view. Defaults to always-true. Inherited attachments shown only by another
   * context's link are made view-only so managing them stays in their own
   * context (#1043).
   */
  canModify?: (attachment: Attachment) => boolean;
  /**
   * When provided, a `booking` entity link renders as a clickable work-order
   * number chip that invokes this with the booking id — used on the vehicle
   * tab to show and open the source work order (#1043). The chip label is the
   * stored work-order number, or a non-number placeholder when none exists
   * (#2907).
   */
  onOpenWorkOrder?: (bookingId: string) => void;
  defaultTags?: string[];
  cardVariant?: 'elevation' | 'outlined';
  /**
   * The entity this card belongs to (#3123). With several `entityLinks`, files
   * linked to it are listed first as the card's own; the rest (e.g. the
   * vehicle's files from other work orders) go under a collapsed heading so
   * staff can tell which is which. Defaults to the first entity link.
   */
  primaryEntity?: EntityRef;
  /**
   * Extra links the upload dialog offers as checkboxes, off by default (#3123):
   * e.g. "also show on every work order of this vehicle".
   */
  optionalUploadLinks?: OptionalUploadLink[];
}

function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
    return <TableChartIcon />;
  if (
    mimeType.includes('document') ||
    mimeType.includes('msword') ||
    mimeType.includes('text/')
  )
    return <DescriptionIcon />;
  return <InsertDriveFileIcon />;
}

function getEntityLabel(
  entityType: AttachmentEntityType,
  t: (key: string) => string,
  tc: (key: string) => string,
): string {
  const labelMap: Record<AttachmentEntityType, string> = {
    booking: t('attachments.entityBooking'),
    vehicle: t('attachments.entityVehicle'),
    customer: t('attachments.entityCustomer'),
    inspection: t('attachments.entityInspection'),
    person: tc('attachments.entityPerson'),
    company: tc('attachments.entityCompany'),
    courtesy_loan: tc('attachments.entityCourtesyLoan'),
  };
  return labelMap[entityType] ?? entityType;
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentsCard({
  entityLinks,
  uploadEntityLinks,
  canModify,
  onOpenWorkOrder,
  defaultTags,
  cardVariant = 'elevation',
  primaryEntity,
  optionalUploadLinks,
}: AttachmentsCardProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const uploadLinks = uploadEntityLinks ?? entityLinks;
  const canModifyAttachment = canModify ?? (() => true);

  const { data: attachments, isLoading } = useMultiEntityAttachments(entityLinks);
  const deleteAttachment = useDeleteAttachment();

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [detailDialogAttachment, setDetailDialogAttachment] = useState<Attachment | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Attachment | null>(null);
  const [relatedOpen, setRelatedOpen] = useState(false);

  // Own vs. related split (#3123). Only meaningful when the card fetches by
  // more than one entity; a single-entity card lists everything as its own.
  const primary = primaryEntity ?? entityLinks[0];
  const isOwn = (attachment: Attachment): boolean =>
    !primary || attachment.entityLinks.some(
      (link) => link.entityType === primary.entityType && link.entityId === primary.entityId,
    );
  const ownAttachments = entityLinks.length > 1 ? (attachments ?? []).filter(isOwn) : (attachments ?? []);
  const relatedAttachments = entityLinks.length > 1 ? (attachments ?? []).filter((a) => !isOwn(a)) : [];

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteAttachment.mutateAsync(deleteConfirm.id);
      notify.success(t('attachments.deleted'));
    } catch {
      notify.error(t('attachments.deleteFailed'));
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const result = await api.get<{ downloadUrl: string; fileName: string }>(
        `/attachments/${attachment.id}/download-url`
      );
      const a = document.createElement('a');
      a.href = result.downloadUrl;
      a.download = result.fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      notify.error(t('attachments.uploadFailed'));
    }
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');

  const renderGrid = (list: Attachment[]) => (
    <ImageList cols={3} gap={8} sx={{ mt: 0, mb: 0 }}>
            {list.map((attachment) => (
              <ImageListItem
                key={attachment.id}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                tabIndex={0}
                aria-label={t('attachments.openPreview', { fileName: attachment.originalFileName })}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    setDetailDialogAttachment(attachment);
                  }
                }}
                onClick={() => setDetailDialogAttachment(attachment)}
              >
                {isImage(attachment.mimeType) && attachment.thumbnailUrl ? (
                  <img
                    src={attachment.thumbnailUrl}
                    alt={attachment.originalFileName}
                    loading="lazy"
                    style={{
                      height: 120,
                      objectFit: 'cover',
                      width: '100%',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 120,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.hover',
                      gap: 0.5,
                    }}
                  >
                    {getFileIcon(attachment.mimeType)}
                    <Typography variant="caption" noWrap sx={{ maxWidth: '90%', px: 1 }}>
                      {attachment.originalFileName}
                    </Typography>
                    {attachment.fileSizeBytes && (
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(attachment.fileSizeBytes)}
                      </Typography>
                    )}
                  </Box>
                )}
                <ImageListItemBar
                  sx={{ '& .MuiImageListItemBar-title': { fontSize: '0.75rem' } }}
                  title={attachment.originalFileName}
                  subtitle={
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                      {attachment.source === 'inbound_email' && (
                        <Chip
                          label={t('attachments.sourceEmail')}
                          size="small"
                          color="info"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                      {attachment.entityLinks.map((link) => {
                        const isWorkOrderLink = link.entityType === 'booking' && !!onOpenWorkOrder;
                        return (
                          <Chip
                            key={link.id}
                            label={isWorkOrderLink ? (link.workOrderNumber ?? t('noWorkOrderNumber')) : getEntityLabel(link.entityType, t, tc)}
                            size="small"
                            variant="outlined"
                            clickable={isWorkOrderLink}
                            onClick={isWorkOrderLink ? (e) => {
                              e.stopPropagation();
                              onOpenWorkOrder(link.entityId);
                            } : undefined}
                            sx={{ height: 18, fontSize: '0.65rem', color: 'grey.300', borderColor: 'grey.600' }}
                          />
                        );
                      })}
                      {attachment.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      ))}
                    </Box>
                  }
                  actionIcon={
                    <Box sx={{ display: 'flex' }}>
                      <Tooltip title={t('attachments.download')}>
                        <IconButton
                          size="small"
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDownload(attachment);
                          }}
                          aria-label={t('attachments.download')}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canModifyAttachment(attachment) && (
                        <Tooltip title={tc('delete')}>
                          <IconButton
                            size="small"
                            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(attachment);
                            }}
                            aria-label={tc('delete')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
  );

  return (
    <>
      <Card variant={cardVariant} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachFileIcon fontSize="small" color="action" />
              <Typography variant="h6">
                {t('attachments.title')}
              </Typography>
              {attachments && attachments.length > 0 && (
                <Chip
                  label={attachments.length}
                  size="small"
                  color="default"
                />
              )}
            </Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setUploadDialogOpen(true)}
            >
              {t('attachments.uploadFile')}
            </Button>
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!isLoading && (!attachments || attachments.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {t('attachments.noAttachments')}
            </Typography>
          )}

          {!isLoading && attachments && attachments.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {ownAttachments.length > 0 ? renderGrid(ownAttachments) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  {t('attachments.noOwnAttachments')}
                </Typography>
              )}
              {relatedAttachments.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => setRelatedOpen((open) => !open)}
                    endIcon={<ExpandMoreIcon sx={{ transform: relatedOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
                    aria-expanded={relatedOpen}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    {t('attachments.relatedFromVehicle', { count: relatedAttachments.length })}
                  </Button>
                  <Collapse in={relatedOpen} unmountOnExit>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {t('attachments.relatedFromVehicleHint')}
                    </Typography>
                    {renderGrid(relatedAttachments)}
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <AttachmentUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        entityLinks={uploadLinks}
        defaultTags={defaultTags}
        optionalLinks={optionalUploadLinks ?? []}
      />

      {/* Detail/Preview Dialog */}
      {detailDialogAttachment && (
        <AttachmentDetailDialog
          open
          attachment={detailDialogAttachment}
          galleryAttachments={ownAttachments}
          onNavigate={setDetailDialogAttachment}
          readOnly={!canModifyAttachment(detailDialogAttachment)}
          onClose={() => setDetailDialogAttachment(null)}
          onDownload={handleDownload}
          onDelete={(a) => {
            setDetailDialogAttachment(null);
            setDeleteConfirm(a);
          }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteConfirm}
        title={tc('delete')}
        message={t('attachments.deleteConfirm')}
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
        loading={deleteAttachment.isPending}
      />
    </>
  );
}
